"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { Lang } from "@/i18n-config";
import { usePublicData } from "@/services/public-data";
import {
  UserLimitationsType,
  userLimitationsSchema,
} from "@/types/user-limitations";

import { RoboAdvisorIcon } from "ui/icon/features";

import { NavbarList } from "./NavbarList";
import { UserPopover } from "./UserPopover";
import { MobilePopover } from "./mobile-popover/MobilePopover";
import { SymbolSearch } from "./symbol-search/SymbolSearch";

const WatchlistToggle = dynamic(
  () =>
    import("./watchlist-toggle/WatchlistToggle").then(
      (mod) => mod.WatchlistToggle
    ),
  { ssr: false }
);

type Props = {
  lang: Lang;
};

export const MyNavbar = ({ lang }: Props) => {
  const customLogoSrc = useCustomLogoSrc();

  return (
    <header className="sticky top-0 z-[99999] flex h-[var(--my-navbar-height)] select-none items-center border-b border-b-zinc-100/20 bg-gradient-to-l from-zinc-900 to-sky-700 px-4 md:px-8 lg:px-12">
      <nav className="mx-auto flex w-full max-w-[var(--my-navbar-width)] items-center">
        <div className="flex items-center">
          <Link
            href={`/${lang}${links.myAnalysisSearch}`}
            className="mr-4 xl:mr-8"
            aria-label="go to analysis search page"
          >
            {customLogoSrc ? (
              <Image
                src={customLogoSrc}
                width={100}
                height={20}
                alt="Custom My Navbar Icon"
                className="aspect-5 overflow-hidden object-cover object-center"
              />
            ) : (
              <RoboAdvisorIcon width={30} height={30} className="fill-white" />
            )}
          </Link>
          <NavbarList
            lang={lang}
            className="hidden xl:flex xl:items-center xl:gap-3"
          />
        </div>
        <div className="ml-auto flex w-full max-w-sm items-center gap-6">
          <SymbolSearch />
          <UserPopover className="hidden xl:block" />
          <MobilePopover className="block xl:hidden" />
          <WatchlistToggle />
        </div>
      </nav>
    </header>
  );
};

const useCustomLogoSrc = () => {
  const { profile } = useAuth();
  const { data: userLimitations } = usePublicData<UserLimitationsType>({
    itemName: "user-limitations",
    schema: userLimitationsSchema,
  });

  const logoConfig = userLimitations?.["my-logo"]?.data;
  if (!logoConfig) return null;

  return logoConfig.find((config: { user: string; src: string }) =>
    profile?.name?.startsWith(config.user)
  )?.src;
};
